/**
 * Supabase Client Setup
 * 
 * Initialisiert den Supabase Client für Auth-Operationen.
 * Nutzt SUPABASE_URL und SUPABASE_KEY aus Backend Config.
 * 
 * Die Config wird vom Backend-Endpoint /api/config/supabase geholt,
 * der die Werte aus den Environment Variables (SUPABASE_URL, SUPABASE_KEY) liest.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import axios from 'axios';

// Supabase Config vom Backend holen
let supabaseConfig: { url: string; key: string } | null = null;
let configPromise: Promise<{ url: string; key: string }> | null = null;

export async function getSupabaseConfig(): Promise<{ url: string; key: string }> {
  if (supabaseConfig) {
    return supabaseConfig;
  }

  if (!configPromise) {
    configPromise = (async () => {
      try {
        // WICHTIG: Nutze direkte Axios-Instanz statt apiClient, 
        // um Circular Dependency (Interceptor -> getSupabaseClient -> config) zu vermeiden!
        const response = await axios.get('/api/config/supabase', {
          headers: { 'Content-Type': 'application/json' }
        });
        supabaseConfig = {
          url: response.data.url,
          key: response.data.key,
        };
        return supabaseConfig;
      } catch (error) {
        console.error('Failed to load Supabase config from backend:', error);
        throw new Error('Supabase configuration not available. Please ensure SUPABASE_URL and SUPABASE_KEY are set in backend environment.');
      }
    })();
  }

  return configPromise;
}

// Supabase Client erstellen (lazy initialization, race-condition-safe)
let clientInstance: SupabaseClient | null = null;
let clientPromise: Promise<SupabaseClient> | null = null;

export async function getSupabaseClient(): Promise<SupabaseClient> {
  if (clientInstance) {
    return clientInstance;
  }

  if (!clientPromise) {
    clientPromise = (async () => {
      const { url, key } = await getSupabaseConfig();

      if (!url || !key) {
        throw new Error('Supabase URL and Key must be configured.');
      }

      clientInstance = createClient(url, key, {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
        },
      });

      return clientInstance;
    })();
  }

  return clientPromise;
}

// Export für direkte Nutzung (muss async aufgerufen werden)
export { getSupabaseClient as supabaseClient };
export default getSupabaseClient;
